import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, X, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../services/api';
import './AdminUI.css';
import AdminTopbarPortal from './AdminTopbarPortal';

export default function AdminOptionSets() {
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', required: false, multiSelect: false, choices: [] });
  const [newChoice, setNewChoice] = useState({ name: '', price: '' });
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const load = async () => {
    const data = await api.getOptionSets().catch(() => []);
    setSets(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm({ name: '', description: '', required: false, multiSelect: false, choices: [] });
    setNewChoice({ name: '', price: '' });
    setEditing(null);
    setError('');
    setShowForm(true);
  };

  const openEdit = (set) => {
    setForm({ ...set });
    setEditing(set.id);
    setError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setError('');
  };

  const addChoice = () => {
    if (!newChoice.name) return;
    const choice = { id: `c_${Date.now()}`, name: newChoice.name, price: parseFloat(newChoice.price || '0') };
    setForm(prev => ({ ...prev, choices: [...prev.choices, choice] }));
    setNewChoice({ name: '', price: '' });
  };

  const removeChoice = (id) => {
    setForm(prev => ({ ...prev, choices: prev.choices.filter(c => c.id !== id) }));
  };

  const handleSave = async () => {
    if (!form.name) { setError('Name is required'); return; }
    if (form.choices.length === 0) { setError('Add at least one choice'); return; }
    setError('');
    try {
      if (editing) {
        await api.updateOptionSet(editing, form);
      } else {
        await api.createOptionSet(form);
      }
      await load();
      setShowForm(false);
      setEditing(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this option set? Items using it will lose this option.')) return;
    await api.deleteOptionSet(id);
    load();
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" /></div>;

  const totalPages = Math.max(1, Math.ceil(sets.length / pageSize));
  const visibleSets = sets.slice((page - 1) * pageSize, page * pageSize);
  const changePage = nextPage => {
    setPage(nextPage);
    setExpanded(null);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  return (
    <div className="admin-view space-y-4">
      <AdminTopbarPortal><div className="admin-topbar-controls">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Reusable Option Sets</h2>
          <p className="text-gray-500 text-sm">Create option sets once, apply to multiple items</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors">
          <Plus className="w-4 h-4" /> New Option Set
        </button>
      </div></AdminTopbarPortal>

      {showForm && (
        <div className="admin-drawer-backdrop" onMouseDown={closeForm}>
        <div className="admin-drawer admin-option-set-drawer" onMouseDown={event => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={editing ? 'Edit option set' : 'New option set'}>
          <div className="admin-drawer-heading">
            <div><span>Option set</span><h3>{editing ? 'Edit option set' : 'New option set'}</h3></div>
            <button onClick={closeForm} aria-label="Close option set editor"><X /></button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500 mb-1 block">Option Set Name *</label>
              <input type="text" placeholder="e.g. Spice Level, Pizza Toppings, Sauce" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500 mb-1 block">Description (optional)</label>
              <input type="text" placeholder="e.g. Choose your preferred spice level" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition-colors">
              <input type="checkbox" checked={form.required} onChange={e => setForm(p => ({ ...p, required: e.target.checked }))} className="w-4 h-4 rounded accent-amber-600" />
              <div>
                <p className="text-sm font-semibold text-gray-700">Required</p>
                <p className="text-xs text-gray-400">Customer must choose</p>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition-colors">
              <input type="checkbox" checked={form.multiSelect} onChange={e => setForm(p => ({ ...p, multiSelect: e.target.checked }))} className="w-4 h-4 rounded accent-amber-600" />
              <div>
                <p className="text-sm font-semibold text-gray-700">Multi-select</p>
                <p className="text-xs text-gray-400">Allow multiple choices</p>
              </div>
            </label>
          </div>

          {/* Choices */}
          <div className="mb-4">
            <label className="text-xs font-semibold text-gray-700 mb-2 block">Choices *</label>
            <div className="space-y-2 mb-3">
              {form.choices.map(c => (
                <div key={c.id} className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                  <span className="flex-1 text-sm font-medium text-gray-800">{c.name}</span>
                  {c.price > 0 && <span className="text-xs text-green-700 font-semibold">+£{c.price.toFixed(2)}</span>}
                  <button onClick={() => removeChoice(c.id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" placeholder="Choice name" value={newChoice.name} onChange={e => setNewChoice(p => ({ ...p, name: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addChoice()}
                className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
              <input type="number" placeholder="Extra £" step="0.01" value={newChoice.price} onChange={e => setNewChoice(p => ({ ...p, price: e.target.value }))}
                className="w-24 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
              <button onClick={addChoice} className="px-4 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-amber-100 hover:text-amber-800 transition-colors">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {error && <p className="text-red-500 text-xs mb-3">{error}</p>}
          <div className="admin-drawer-footer">
            <button onClick={handleSave} className="bg-gray-900 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors">{editing ? 'Update' : 'Save Option Set'}</button>
            <button onClick={closeForm} className="px-6 py-2.5 text-gray-600 text-sm font-semibold hover:bg-gray-100 rounded-xl transition-colors">Cancel</button>
          </div>
        </div>
        </div>
      )}

      {sets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-400">
          <p className="font-medium">No option sets yet</p>
          <p className="text-sm mt-1">Create option sets to reuse across menu items</p>
        </div>
      ) : (
        <div className="admin-option-set-table-shell">
          <table className="admin-option-set-table">
            <thead>
              <tr>
                <th>Option set</th>
                <th>Requirement</th>
                <th>Selection</th>
                <th>Choices</th>
                <th className="admin-option-set-actions-heading">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleSets.map(set => (
                <React.Fragment key={set.id}>
                  <tr className={`admin-option-set-table-row ${expanded === set.id ? 'is-expanded' : ''}`}>
                    <td data-label="Option set">
                      <strong>{set.name}</strong>
                      {set.description && <p>{set.description}</p>}
                    </td>
                    <td data-label="Requirement">
                      <span className={`admin-option-set-pill ${set.required ? 'is-required' : 'is-optional'}`}>{set.required ? 'Required' : 'Optional'}</span>
                    </td>
                    <td data-label="Selection">
                      <span className={`admin-option-set-pill ${set.multiSelect ? 'is-multiple' : 'is-single'}`}>{set.multiSelect ? 'Multi-select' : 'Single'}</span>
                    </td>
                    <td data-label="Choices"><span className="admin-option-set-choice-count">{set.choices?.length || 0} choices</span></td>
                    <td data-label="Actions" className="admin-option-set-actions-cell">
                      <div className="admin-option-set-actions">
                        <button onClick={() => setExpanded(expanded === set.id ? null : set.id)} aria-label={expanded === set.id ? `Collapse ${set.name}` : `Expand ${set.name}`}>
                          {expanded === set.id ? <ChevronUp /> : <ChevronDown />}
                        </button>
                        <button onClick={() => openEdit(set)} aria-label={`Edit ${set.name}`}><Edit /></button>
                        <button onClick={() => handleDelete(set.id)} aria-label={`Delete ${set.name}`} className="is-delete"><Trash2 /></button>
                      </div>
                    </td>
                  </tr>
                  {expanded === set.id && (
                    <tr className="admin-option-set-detail-row">
                      <td colSpan="5">
                        <div className="admin-option-set-choice-list">
                          {set.choices?.map(c => (
                            <div key={c.id}>
                              <span>{c.name}</span>
                              {c.price > 0 && <strong>+£{c.price.toFixed(2)}</strong>}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {totalPages > 1 && (
        <div className="admin-pagination">
          <button disabled={page === 1} onClick={() => changePage(page - 1)}>Previous</button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => changePage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
